import { useCallback, useEffect, useReducer, useRef } from 'react';
import { trackFunnel } from '@react/funnel';
import { ApiError, CheckoutApi } from '../api/checkoutApi';
import { BasketSummary, VatValidateResponse, VatStatus } from '../types';

/**
 * Stavový automat VIES validace DIČ/IČ DPH:
 *
 *   idle → validating → validPayer | invalid | unavailable → manualChoice
 *                     ↘ rateLimited (vlastní 429 API, sám to zkusí znovu) | failed
 *
 * - změna DIČ (debounce 1,5 s / blur) spouští validaci a ruší in-flight request
 *   jiného DIČ; běžící dotaz na totéž DIČ nechá doběhnout (VIES odpovídá až ~10 s)
 * - ruční volba plátce jen při výpadku VIES hlášeném serverem (status unavailable);
 *   429 ani síťová chyba ji nenabízí — server by ji odmítl (409 CHOICE_NOT_ALLOWED)
 * - validace při načtení stránky (validateInitial) opakuje pokusy při výpadku
 *   VIES — bývají to přechodné limity, které za chvíli zmizí
 * - submit formuláře je blokován ve stavech validating, rateLimited a unavailable-bez-volby
 * - server drží autoritativní stav (e_checkout_vat_state) a při submitu
 *   re-validuje, takže UI stav je jen pro UX
 */

export type VatUiState =
    | { kind: 'idle' }
    | { kind: 'validating'; dic: string }
    | { kind: 'validPayer'; dic: string; message: string | null; countryMismatch: boolean }
    | { kind: 'invalid'; dic: string; message: string | null }
    | { kind: 'notApplicable'; dic: string }
    | { kind: 'unavailable'; dic: string; message: string | null }
    | { kind: 'rateLimited'; dic: string }
    | { kind: 'failed'; dic: string }
    | { kind: 'manualChoice'; dic: string; payer: boolean };

type Action =
    | { type: 'reset' }
    | { type: 'validating'; dic: string }
    | { type: 'result'; dic: string; response: VatValidateResponse }
    | { type: 'error'; dic: string; message: string | null }
    | { type: 'rateLimited'; dic: string }
    | { type: 'failed'; dic: string }
    | { type: 'manual'; dic: string; payer: boolean };

function reducer(state: VatUiState, action: Action): VatUiState {
    switch (action.type) {
        case 'reset':
            return { kind: 'idle' };
        case 'validating':
            return { kind: 'validating', dic: action.dic };
        case 'result': {
            const r = action.response;
            switch (r.status) {
                case 'valid':
                    return { kind: 'validPayer', dic: r.normalizedDic, message: r.message, countryMismatch: r.countryMismatch };
                case 'not_applicable':
                    return { kind: 'notApplicable', dic: r.normalizedDic };
                case 'unavailable':
                    return { kind: 'unavailable', dic: r.normalizedDic, message: r.message };
                default:
                    return { kind: 'invalid', dic: r.normalizedDic, message: r.message };
            }
        }
        case 'error':
            return { kind: 'unavailable', dic: action.dic, message: action.message };
        case 'rateLimited':
            return { kind: 'rateLimited', dic: action.dic };
        case 'failed':
            return { kind: 'failed', dic: action.dic };
        case 'manual':
            return { kind: 'manualChoice', dic: action.dic, payer: action.payer };
    }
}

export interface UseVatMachine {
    state: VatUiState;
    /**
     * validace se spouští z onChange (debounce) i onBlur (okamžitě);
     * force = tlačítko „ověřit přes VIES" — ověří i nezměněné DIČ
     */
    onDicChanged: (dic: string, immediate?: boolean, force?: boolean) => void;
    /** ověření předvyplněného DIČ při načtení stránky — při výpadku VIES opakuje */
    validateInitial: (dic: string) => void;
    chooseManual: (payer: boolean) => Promise<void>;
    /** submit se nesmí odeslat, dokud je validace v běhu nebo chybí ruční volba */
    blocksSubmit: boolean;
    /** po 409 VAT_CHOICE_REQUIRED ze submitu přepne UI do volby */
    forceManualChoice: (dic: string) => void;
}

const DEBOUNCE_MS = 1500;
const LOAD_RETRY_MS = 5000;
const LOAD_MAX_ATTEMPTS = 10;
const RATE_LIMIT_MAX_RETRIES = 2;
const RATE_LIMIT_DEFAULT_WAIT_S = 30;

function retryAfterMs(error: ApiError): number {
    const seconds = Number(error.context?.retryAfter);
    const wait = Number.isFinite(seconds) && seconds > 0 ? Math.min(seconds, 60) : RATE_LIMIT_DEFAULT_WAIT_S;

    return (wait + 1) * 1000;
}

function trackVatResult(status: VatValidateResponse['status']): void {
    if (status === 'valid') {
        trackFunnel('CheckoutVatValid');
    } else if (status === 'unavailable') {
        trackFunnel('CheckoutVatUnavailable');
    } else if (status !== 'not_applicable') {
        trackFunnel('CheckoutVatInvalid');
    }
}

export function useVatMachine(
    api: CheckoutApi,
    initialDic: string | null,
    initialStatus: VatStatus,
    onCompanyData: (response: VatValidateResponse) => void,
    onSummary: (summary: BasketSummary) => void,
): UseVatMachine {
    const [state, dispatch] = useReducer(reducer, undefined, (): VatUiState => {
        if (initialDic && initialStatus === 'valid') {
            return { kind: 'validPayer', dic: initialDic, message: null, countryMismatch: false };
        }
        if (initialDic && (initialStatus === 'manual_payer' || initialStatus === 'manual_nonpayer')) {
            return { kind: 'manualChoice', dic: initialDic, payer: initialStatus === 'manual_payer' };
        }
        return { kind: 'idle' };
    });

    const abortRef = useRef<AbortController | null>(null);
    const timerRef = useRef<number | null>(null);
    const retryTimerRef = useRef<number | null>(null);
    const lastValidatedRef = useRef<string | null>(initialDic);
    /** DIČ, na které právě běží request (null = nic neběží, i během čekání na opakování) */
    const inFlightRef = useRef<string | null>(null);
    const rateLimitRetriesRef = useRef(0);

    const clearRetryTimer = () => {
        if (retryTimerRef.current !== null) {
            window.clearTimeout(retryTimerRef.current);
            retryTimerRef.current = null;
        }
    };

    const runValidation = useCallback((dic: string, retriesLeft = 0, rateLimitRetry = false) => {
        abortRef.current?.abort();
        clearRetryTimer();

        const cleaned = dic.replace(/[\s.\-]/g, '').toUpperCase();
        if (cleaned.length < 4) {
            dispatch({ type: 'reset' });
            return;
        }

        if (!rateLimitRetry) {
            rateLimitRetriesRef.current = 0;
        }

        const controller = new AbortController();
        abortRef.current = controller;
        lastValidatedRef.current = cleaned;
        inFlightRef.current = cleaned;
        dispatch({ type: 'validating', dic: cleaned });

        // Výpadek VIES při načtení bývá jen limit počtu dotazů — opakujeme,
        // stav zůstává validating (uživatel vidí, že ověření běží)
        const scheduleRetry = () => {
            retryTimerRef.current = window.setTimeout(() => runValidation(dic, retriesLeft - 1), LOAD_RETRY_MS);
        };

        api.vatValidate(cleaned, controller.signal)
            .finally(() => {
                if (abortRef.current === controller) {
                    inFlightRef.current = null;
                }
            })
            .then((response) => {
                if (controller.signal.aborted) {
                    return;
                }
                if (response.status === 'unavailable' && retriesLeft > 0) {
                    scheduleRetry();
                    return;
                }
                dispatch({ type: 'result', dic: cleaned, response });
                trackVatResult(response.status);
                onCompanyData(response);
                onSummary(response.basketSummary);
            })
            .catch((error) => {
                if (controller.signal.aborted) {
                    return;
                }
                // 429 = náš vlastní limit dotazů, ne výpadek VIES → počkat na nové okno a zkusit znovu
                if (error instanceof ApiError && error.status === 429) {
                    trackFunnel('CheckoutVatRateLimited');
                    dispatch({ type: 'rateLimited', dic: cleaned });
                    if (rateLimitRetriesRef.current < RATE_LIMIT_MAX_RETRIES) {
                        rateLimitRetriesRef.current += 1;
                        retryTimerRef.current = window.setTimeout(
                            () => runValidation(dic, retriesLeft, true),
                            retryAfterMs(error),
                        );
                    } else {
                        dispatch({ type: 'failed', dic: cleaned });
                    }
                    return;
                }
                if (retriesLeft > 0) {
                    scheduleRetry();
                    return;
                }
                dispatch({ type: 'failed', dic: cleaned });
                trackFunnel('CheckoutVatFailed');
            });
    }, [api, onCompanyData, onSummary]);

    const validateInitial = useCallback((dic: string) => {
        runValidation(dic, LOAD_MAX_ATTEMPTS - 1);
    }, [runValidation]);

    const onDicChanged = useCallback((dic: string, immediate = false, force = false) => {
        // naplánované opakování (429 / výpadek) rušit až při skutečně nové validaci —
        // blur beze změny by ho zrušil a stav rateLimited by už nic neopustilo
        if (timerRef.current !== null) {
            window.clearTimeout(timerRef.current);
            timerRef.current = null;
        }

        const cleaned = dic.replace(/[\s.\-]/g, '').toUpperCase();

        // blur během ověřování téhož DIČ: nerušit běžící dotaz (abort = status 0 a nové čekání na VIES)
        if (!force && cleaned === inFlightRef.current) {
            return;
        }

        if (cleaned === '') {
            clearRetryTimer();
            abortRef.current?.abort();
            lastValidatedRef.current = null;
            dispatch({ type: 'reset' });
            // server smaže VAT stav košíku a vrátí souhrn bez zvýraznění
            void api.vatValidate('').then((r) => onSummary(r.basketSummary)).catch(() => undefined);
            return;
        }

        // beze změny (blur po úspěšné validaci) nevalidovat znovu
        if (!immediate && cleaned === lastValidatedRef.current && state.kind !== 'idle') {
            return;
        }

        if (immediate) {
            if (!force && cleaned === lastValidatedRef.current && state.kind !== 'idle' && state.kind !== 'validating') {
                return;
            }
            runValidation(dic);
        } else {
            clearRetryTimer();
            timerRef.current = window.setTimeout(() => runValidation(dic), DEBOUNCE_MS);
        }
    }, [runValidation, state.kind]);

    const chooseManual = useCallback(async (payer: boolean) => {
        const dic = state.kind === 'idle' ? '' : state.dic;
        if (dic === '') {
            return;
        }

        try {
            const result = await api.vatChoice(payer, dic);
            dispatch({ type: 'manual', dic, payer });
            trackFunnel('CheckoutVatManualChoice', { payer });
            onSummary(result.basketSummary);
        } catch {
            // Volba odmítnuta (409 = pro toto DIČ nebyl výpadek) → znovu ověřit
            // přes VIES; validace stav správně přenastaví (valid/invalid/unavailable/rateLimited)
            runValidation(dic);
        }
    }, [api, state, onSummary, runValidation]);

    const forceManualChoice = useCallback((dic: string) => {
        dispatch({ type: 'error', dic, message: null });
    }, []);

    useEffect(() => () => {
        abortRef.current?.abort();
        if (timerRef.current !== null) {
            window.clearTimeout(timerRef.current);
        }
        if (retryTimerRef.current !== null) {
            window.clearTimeout(retryTimerRef.current);
        }
    }, []);

    return {
        state,
        onDicChanged,
        validateInitial,
        chooseManual,
        // failed neblokuje: o DPH rozhoduje server při submitu (VatDecisionService, živé VIES)
        blocksSubmit: state.kind === 'validating' || state.kind === 'unavailable' || state.kind === 'rateLimited',
        forceManualChoice,
    };
}
